import Api from 'services/api';

// Gerenciador de Petições do Dr. Licita 3.0 (página do beta):
// listagem unificada (pastas + peças + arquivos do usuário) por pasta,
// via proxy Rails para os endpoints /v1/petitions do drlicita.

export type PeticaoItemType = 'pasta' | 'peticao' | 'arquivo';

export interface PeticaoItem {
  type: PeticaoItemType;
  id: number;
  name: string;
  updated_at: string;
  size?: number;
  mine?: boolean;
  shared?: boolean;
  owner?: string | null;
}

export interface PeticoesFolder {
  id: number;
  name: string;
}

export interface PeticoesListing {
  folder: PeticoesFolder | null;
  items: PeticaoItem[];
}

export const fetchPeticoesItems = async (
  folderId?: number | null,
  scope?: 'todos' | 'mine',
): Promise<PeticoesListing | null> => {
  const params = [];
  if (folderId) params.push(`folder_id=${folderId}`);
  if (scope) params.push(`scope=${scope}`);
  const query = params.length ? `?${params.join('&')}` : '';
  const response = await Api.get(`/dr_licita/peticoes/items${query}`);
  if (response?.status === 200) return response.data;
  return null;
};

// --- Compartilhamento com o time ---

export interface PeticoesShare {
  id: number;
  item_type: PeticaoItemType;
  item_id: number;
  target_conlicita_id: number | null;
}

export interface MembroEquipe {
  conlicita_id: number;
  name: string;
  email: string;
}

export const fetchPeticoesShares = async (
  itemType: PeticaoItemType,
  itemId: number,
): Promise<PeticoesShare[]> => {
  const response = await Api.get(
    `/dr_licita/peticoes/shares?item_type=${itemType}&item_id=${itemId}`,
  );
  if (response?.status === 200) return response.data.shares || [];
  return [];
};

export const sharePeticoesItem = async (
  itemType: PeticaoItemType,
  itemId: number,
  targetConlicitaId: number | null,
): Promise<PeticoesShare | null> => {
  const response = await Api.post('/dr_licita/peticoes/shares', {
    item_type: itemType,
    item_id: itemId,
    target_conlicita_id: targetConlicitaId,
  });
  if (response?.status === 200) return response.data.share;
  return null;
};

export const unsharePeticoesItem = async (shareId: number): Promise<boolean> => {
  const response = await Api.delete(`/dr_licita/peticoes/shares/${shareId}`);
  return response?.status === 200;
};

export const fetchEquipe = async (): Promise<MembroEquipe[]> => {
  const response = await Api.get('/dr_licita/peticoes/equipe');
  if (response?.status === 200) return response.data.membros || [];
  return [];
};

export interface PeticaoDocumentoView {
  id: number;
  title: string;
  mine: boolean;
  owner: string | null;
  blocks: Array<{ bloc_cd_id: number; bloc_tx_title: string | null; bloc_tx_body: string }>;
}

export const viewPeticaoDocument = async (
  documentId: number,
): Promise<PeticaoDocumentoView | null> => {
  const response = await Api.get(`/dr_licita/peticoes/documents/${documentId}/view`);
  if (response?.status === 200) return response.data.document;
  return null;
};

export const createPeticoesFolder = async (name: string): Promise<PeticoesFolder | null> => {
  const response = await Api.post('/dr_licita/peticoes/folders', { name });
  if (response?.status === 200) return response.data.folder;
  return null;
};

export const renamePeticoesFolder = async (id: number, name: string): Promise<boolean> => {
  const response = await Api.put(`/dr_licita/peticoes/folders/${id}`, { name });
  return response?.status === 200;
};

export const deletePeticoesFolder = async (id: number): Promise<boolean> => {
  const response = await Api.delete(`/dr_licita/peticoes/folders/${id}`);
  return response?.status === 200;
};

export const movePeticoesItem = async (
  itemType: PeticaoItemType,
  itemId: number,
  folderId: number | null,
): Promise<boolean> => {
  const response = await Api.post('/dr_licita/peticoes/move', {
    item_type: itemType,
    item_id: itemId,
    folder_id: folderId,
  });
  return response?.status === 200;
};

export const renamePeticaoDocument = async (id: number, title: string): Promise<boolean> => {
  const response = await Api.put(`/dr_licita/peticoes/documents/${id}`, { title });
  return response?.status === 200;
};

export const uploadPeticoesFile = async (
  file: File,
  folderId?: number | null,
): Promise<boolean> => {
  const formData = new FormData();
  formData.append('file', file);
  if (folderId) formData.append('folder_id', String(folderId));
  const response = await Api.post('/dr_licita/peticoes/files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response?.status === 200;
};

export const deletePeticoesFile = async (id: number): Promise<boolean> => {
  const response = await Api.delete(`/dr_licita/peticoes/files/${id}`);
  return response?.status === 200;
};

// Editar peça via chat: devolve (ou cria) a conversa vinculada ao documento
export const openPeticaoConversation = async (
  documentId: number,
): Promise<{ id: number } | null> => {
  const response = await Api.post('/dr_licita/chats/from_document', { document_id: documentId });
  if (response?.status === 201) return response.data.chat;
  return null;
};

export const peticoesFileDownloadUrl = (id: number): string =>
  `${process.env.REACT_APP_API_ENDPOINT}/dr_licita/peticoes/files/${id}/download`;
